import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { RESET_STATE } from "@/store/reset-state";

/**
 * The one part of a Receipt Claim kept on the device: the PDF Receipt the
 * participant picked, by its key, with its ticked Selected Pages and the
 * participant it belongs to. Not blacklisted from redux-persist (see
 * `store/store.ts`), so a refresh finds it in local storage and the upload
 * step brings its Page Previews back.
 *
 * Never a signed link, the file's name or the File. The key and the page
 * numbers are useless without the participant's token. Forgotten when a
 * Receipt Claim starts, on Cancel, once the claim is submitted and on logout.
 */
export interface PdfReceiptDraftState {
  /** The participant the PDF Receipt belongs to, null while none is kept. */
  participantId: string | null;
  /** The PDF Receipt's `pdf_url`, null while none is kept. */
  pdfUrl: string | null;
  /** The ticked page numbers. */
  selectedPages: number[];
}

interface KeptPdfReceipt {
  participantId: string;
  pdfUrl: string;
  selectedPages: number[];
}

const initialState: PdfReceiptDraftState = {
  participantId: null,
  pdfUrl: null,
  selectedPages: [],
};

export const pdfReceiptDraftSlice = createSlice({
  name: "pdfReceiptDraft",
  initialState,
  selectors: {
    selectPdfReceiptDraft: (state) => state,
  },
  reducers: {
    keepPdfReceipt: (_state, action: PayloadAction<KeptPdfReceipt>) =>
      action.payload,
    forgetPdfReceipt: () => initialState,
  },
  extraReducers: (builder) => {
    // Logout purges storage, and resetting here too stops the PDF Receipt
    // still in memory being written back to it.
    builder.addCase(RESET_STATE, () => initialState);
  },
});

export const { selectPdfReceiptDraft } = pdfReceiptDraftSlice.selectors;

export const { keepPdfReceipt, forgetPdfReceipt } =
  pdfReceiptDraftSlice.actions;
